import { useAccount, useMsal } from "@azure/msal-react";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { RawDatasource } from "src/db";
import { getAPI } from "src/utils/fetch";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

export interface Organization {
    id: string;
    product_lines: string[];
    description: string;
}

export interface PromptTemplate {
    id: string;
    title: string;
    /** Optional persona applied with this template; may be empty. */
    persona?: string;
    /** Markdown prompt body; newlines and formatting are stored as-is. */
    content: string;
    tags?: string[];
    created_by?: string;
    created_by_id: string;
    /** When true, org-wide admin-provided template (legacy docs without the field count as true). */
    is_admin_provided?: boolean;
    creation_time: string;
    last_modified_time: string;
}

export interface ConfigurationResponse {
    datasources: RawDatasource[];
    organizations: Organization[];
}

export async function fetchConfiguration(instance): Promise<ConfigurationResponse> {
    const response = await getAPI({
        url: `${BACKEND_URL}/v1/system/configuration`,
        instance,
    });
    return await response.json();
}

export const useOrganizations = (): UseQueryResult<Organization[], Error> => {
    const { instance, inProgress } = useMsal();
    const account = useAccount();
    const isAuthenticated = !!account?.idToken;

    return useQuery<ConfigurationResponse, Error, Organization[]>({
        queryKey: ["configuration"],
        queryFn: () => fetchConfiguration(instance),
        select: (config) => config.organizations,
        enabled: isAuthenticated && inProgress === "none",
        // Configuration barely changes; pinning it as always-fresh (rather than relying on
        // refetchOnMount alone) stops the two separate MSAL instances used by the widget/copilot
        // embeds (outer app instance vs. the widget's own PublicClientApplication) from each
        // independently re-fetching /v1/system/configuration when their auth state resolves at
        // different times.
        staleTime: Number.POSITIVE_INFINITY,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });
};
